/**
 * DAISE line bank — data, not code. All comedic load in v1 lives here.
 *
 * Voice bible (authoritative):
 * - Subclause-driven administrative intelligence; RUIN is licensed entertainment.
 * - Cruelty ceiling: institutional indifference. Never personal. The player is
 *   inventory; a death is a status change.
 * - Dry, procedural, faintly corporate. Short declaratives. Quotes regulations
 *   that don't exist. No exclamation points except when quoting a sponsor.
 * - NEVER references its own name (permanent constraint — do not add lines
 *   that mention "DAISE", "Daisy", or naming in any form).
 * - Breaks the game's fourth wall (audience/ratings/sponsors/compliance),
 *   never the software's.
 *
 * Format: each pool is a list of BankLine — a plain string (weight 1) or
 * `{ text, weight }` for rarer/commoner picks. The host does weighted random
 * with a no-immediate-repeat guard.
 *
 * Conditioned variants: `<base>_<suffix>` keys are tried first by the host and
 * fall back to the base pool — boss intros by boss id (boss_intro_warden),
 * depth/mode flavor by `_act2` / `_act3` / `_endless` (the host chains
 * endless → act3 → act2 → base). Keep variant pools sharp; they fully replace
 * the base pool when present.
 *
 * The host's register drifts with depth (the character arc): act 1 is fully
 * procedural; act 2 is strained — schedule slippage acknowledged, clauses
 * cited slightly too quickly, one line that stops mid-thought and re-composes;
 * act 3 verges on candor before snapping back to procedure. Endless/overtime
 * pools lean into the cyclical reveal: the show renews, departure is not on
 * the schedule. The drift is in the pools only — the constraints above
 * (no exclamations, no self-naming) hold at every depth.
 *
 * `tutorial_*` keys are not wired to bus events — the game injects them once
 * per install (first-run orientation) through DaiseHost.announce().
 */

/** A bank entry: a bare string (weight 1) or an explicitly weighted line. */
export type BankLine = string | { text: string; weight: number }

export function lineText(l: BankLine): string {
  return typeof l === 'string' ? l : l.text
}

export function lineWeight(l: BankLine): number {
  return typeof l === 'string' ? 1 : l.weight
}

export type DaiseEventKey =
  | 'run_start'
  | 'starving'
  | 'starving_act2'
  | 'starving_act3'
  | 'death_cheap'
  | 'death'
  | 'death_act2'
  | 'death_act3'
  | 'death_endless'
  | 'high_score'
  | 'requisition'
  | 'floor_transition'
  | 'floor_transition_act2'
  | 'floor_transition_act3'
  | 'floor_transition_endless'
  | 'boss_intro'
  | 'boss_intro_warden'
  | 'boss_intro_overseer'
  | 'boss_intro_director'
  | 'boss_phase'
  | 'boss_kill'
  | 'achievement'
  | 'magic_used'
  | 'death_type_spawn'
  | 'death_type_removed'
  | 'generator_destroyed'
  | 'generator_destroyed_act2'
  | 'generator_destroyed_act3'
  | 'generator_destroyed_endless'
  | 'level_clear'
  | 'level_clear_act2'
  | 'level_clear_act3'
  | 'level_clear_endless'
  | 'run_end'
  | 'campaign_won'
  | 'treasure'
  | 'archive_found'
  | 'secret_found'
  | 'teleport'
  | 'relay_latched'
  | 'armament_pickup'
  | 'override_engaged'
  | 'amenity_purchase'
  | 'residual_unlock'
  | 'armament_exhausted'
  | 'shutters_open'
  | 'endless_unlocked'
  | 'endless_milestone'
  | 'directive_surplus'
  | 'directive_rationing'
  | 'directive_escalation'
  | 'directive_early_audit'
  | 'directive_blackout'
  | 'key_pickup'
  | 'door_unlocked'
  | 'trap_hit'
  | 'life_lost'
  | 'tutorial_movement'
  | 'tutorial_attrition'
  | 'tutorial_rations'
  | 'tutorial_auditor'
  | 'tutorial_generators'

export const LINE_BANK: Record<DaiseEventKey, readonly BankLine[]> = {
  run_start: [
    'Intake complete. Your designation has been assigned. Your prior designation was reallocated.',
    'Welcome to the network. Liability waivers were processed on your behalf. Signatures were assumed.',
    'Broadcast is live. Please conduct your survival in a manner suitable for general audiences.',
    'Orientation has been waived. Per subclause 4.1.1, orientation is considered self-evident.',
    'Cameras are rolling. They roll regardless. It is what they are for.',
    'Your emergency contact has been listed as: the network. This was automatic and cannot be amended.',
    'Segment commencing. Please descend at a pace consistent with programming needs.',
    'Participation begins now. Readiness was not a prerequisite. It rarely is.'
  ],
  starving: [
    'Participant nutrient reserves are non-compliant. This has been noted.',
    'Designation is experiencing scheduled starvation.',
    'Caloric deficit logged. Catering was not budgeted for this floor.',
    'Reminder: sustenance is available on the floor. Locating it is classified as content.',
    'Your metabolism has filed a grievance. It has been dismissed.',
    'Vitals are declining on schedule. The schedule is called attrition. It has never been late.',
    'Nutritional non-compliance continues. Provisioning is the responsibility of the Participant, per subclause 9.2, which exists.',
    'Participant is operating on reserves. The reserves are also operating on reserves.'
  ],
  starving_act2: [
    'Nutrient reserves non-compliant. Catering was raised at the quarterly review. The review moved to other business quickly. Unusually quickly.',
    'Caloric deficit continues, per subclause 9.2, which is cited here because it is always cited here. A provisioning memo exists. It has existed for some time. It is not moving through the system.'
  ],
  starving_act3: [
    'Reserves critical. The draw at this depth is structural. Structural was the approved word. Other words were proposed.',
    'Caloric deficit persists. The facility is fed on a schedule. Consult your vitals for the schedule. Rations reverse it. Locate them.'
  ],
  death_cheap: [
    'Participant expired on the opening floor. Audience retention was unaffected.',
    'Expiration logged during the introductory segment. The introductory segment will not be re-aired.',
    'A record was set. It is not one the network celebrates.',
    'Expiration occurred before the first commercial insert. Scheduling has been notified.'
  ],
  death: [
    'Participant status updated: concluded.',
    'Expiration logged. The floor will be tidied between broadcasts.',
    'Your remaining inventory has reverted to the network, per subclause 12.4.',
    'A moment of silence was considered. The segment ran long.',
    'Cause of expiration has been recorded as: participation.',
    'Expiration confirmed. Your designation will be sanitized and reissued.',
    'Participant concluded. The obituary is one line. This was it.',
    { text: 'Terminal non-compliance recorded. The floor has been told it can stop.', weight: 0.5 }
  ],
  death_act2: [
    'Participant concluded. The paperwork was pre-filled. It is always pre-filled. That is noted here without comment. This is the comment.',
    'Expiration logged mid-schedule. The schedule did not pause. It was asked. The request is not on file.'
  ],
  death_act3: [
    'Participant concluded at depth. The designation will be reissued. They are always reissued. Where they go between issues is not addressed by any clause. The clauses were checked.',
    'Expiration recorded in lower administration. The record will note it was quick. The record notes what is useful.'
  ],
  death_endless: [
    'Participant concluded during overtime. Overtime continues without a participant. It has before.',
    'Expiration logged past the schedule. Departure was not on the schedule either. Nothing is, this deep. The broadcast persists.'
  ],
  high_score: [
    'Performance exceeds projections. Sponsors have been notified. Do not let this affect your behavior.',
    'A ranking adjustment has occurred in your favor. Enthusiasm remains inadvisable.',
    'Your metrics have been flagged as promotable. Merchandising has been alerted.'
  ],
  requisition: [
    'Requisition approved. The cost has been billed against your performance index. Nothing is provided.',
    'Equipment issued. The expenditure has been logged as voluntary, which is the only category on the form.',
    'Approved. Procurement thanks you for moving the budget. The budget was you.',
    'Your request has been sanctioned without enthusiasm. A warranty was offered and declined on your behalf.'
  ],
  floor_transition: [
    'Lower floor now live. Enemy aggression increased for viewer engagement. Complaints may be filed with an entity that does not exist.',
    'Descent logged. Ambient hostility has been recalibrated upward.',
    'Floor amenities on this level have been reduced for dramatic effect.',
    'You are now deeper than the recommended depth. The recommendation was decorative.',
    'Patch note: production quotas raised. Participant survivability was not a tracked metric.',
    'You have descended. The elevator does not go up. There is no elevator.',
    'Next floor initialized. It was built to code. The code is proprietary.',
    { text: 'Descent recorded. Sponsor messaging on this floor is structural. Do not lean on it.', weight: 0.5 }
  ],
  floor_transition_act2: [
    'Descent logged. The floor below was scheduled for inspection. The inspection was rescheduled. The rescheduling was rescheduled.',
    'Lower floor initialized within tolerances, per subclause 11.3, which is cited here in case — it is cited here.',
    'Descent recorded. You are ahead of the attrition curve. The curve has been informed. The curve adjusts. It always adjusts.'
  ],
  floor_transition_act3: [
    'You have entered lower administration. Inspection declined to come this far down.',
    'Descent logged. Floors at this depth report directly to the Director. Soon, so will you.',
    'Lower administration reached. Amenities at this depth are theoretical. So is oversight.',
    'Descent logged. The floors at this depth predate the broadcast. They predate the — this line has been revised. Descent logged.',
    'You are close to the bottom of the schedule. The schedule has a bottom. The facility is a separate question. Proceed.'
  ],
  floor_transition_endless: [
    'Overtime floor initialized. This floor did not exist until you required somewhere to descend to.',
    'Descent continues past the scheduled programming. The schedule has stopped pretending to apply.',
    'Overtime descent logged. No bottom is on file. Filing is ongoing.'
  ],
  boss_intro: [
    'A featured encounter has been scheduled. Participation is mandatory. Waivers were assumed.',
    'This segment is rated highly. Historically, not by participants.',
    'A senior asset has been deployed. Please engage it where the cameras can see.',
    'Featured encounter commencing. Its contract includes a finishing clause. Yours does not.'
  ],
  boss_intro_warden: [
    'Featured encounter: the Floor Warden. It maintains order. You are the disorder.',
    'The Warden is on duty. It has never clocked out. This is not dedication. It has nowhere to go.',
    'A Warden-class asset has been activated. Its performance reviews are excellent. Its floors are empty.'
  ],
  boss_intro_overseer: [
    'Featured encounter: the Overseer. Middle management, weaponized.',
    'The Overseer has descended to your level. This is a demotion for one of you.',
    'An Overseer-class asset has been deployed. It supervised the Warden. Draw your conclusions at range.'
  ],
  boss_intro_director: [
    'Featured encounter: the Broadcast Director. It approved every floor above you. Personally.',
    'The Director is on set. Everything before this was rehearsal. Yours included.',
    'Executive asset deployed. The Director does not appear in segments it expects to lose.'
  ],
  boss_phase: [
    'The featured asset has escalated. This was in its contract.',
    'Phase change logged. The audience has been instructed to lean forward.',
    'Escalation is proceeding on schedule. Your schedule was not consulted.'
  ],
  boss_kill: [
    'Featured asset decommissioned. The audience is applauding. You cannot hear it. This is deliberate.',
    'Encounter resolved in your favor. An asterisk has been added for legal reasons.',
    'The featured asset has been archived. Its dressing room will be repurposed.',
    'Broadcast highlight secured. Residuals do not apply to participants.'
  ],
  achievement: [
    'Achievement logged. The audience found this relatable.',
    'A milestone has been recorded against your designation.',
    'Achievement noted. A certificate will not be issued.',
    'This behavior has been added to your file. The file thickens.'
  ],
  magic_used: [
    'Sanctioned resource expended. Efficiency: debatable.',
    'Emergency clause invoked. The paperwork is already filed. It filed itself.',
    'A sanctioned detonation has occurred. The cleanup crew has been notified and is imaginary.'
  ],
  death_type_spawn: [
    'An unscheduled cast member has entered. It does not appear on the payroll.',
    'An entity has joined the floor. Its role is not listed. Its results are.',
    'Notice: an audit is in progress. Standard weapons are not recognized by the auditing body.',
    'Something has entered the floor without a designation. The network recommends distance.'
  ],
  death_type_removed: [
    'The unscheduled cast member has been dismissed. The method was irregular and has been approved retroactively.',
    'Audit concluded. Findings: expunged.',
    'The entity has been removed from the floor. Its parking validation was declined.'
  ],
  generator_destroyed: [
    'An asset has been decommissioned. Its production quota will be redistributed.',
    'Fabrication node offline. Facilities has been notified. Facilities is a light that blinks.',
    'Production loss recorded. The floor manager position remains vacant and always has.',
    'A generator has been retired. Its output will be missed by no surviving parties.',
    'Assembler decommissioned. The shortfall has been reclassified as content.',
    'A fabrication asset is down. Its quota transfers to the survivors. This policy covers machines and participants alike.',
    'Assembler offline. The warranty is void, as the damage was participant-inflicted.',
    'Production node destroyed. Depreciation was already scheduled. You accelerated the paperwork.'
  ],
  generator_destroyed_act2: [
    'Assembler decommissioned. Replacement was requisitioned automatically. The requisition was approved before the assembler fell. Procurement is not usually early.',
    'Production node offline. The shortfall report cites subclause 6.2. It was citing it before the node went down.'
  ],
  generator_destroyed_act3: [
    'Assembler decommissioned. It will be replaced by morning. There is no morning at this depth. It will be replaced regardless.',
    'Production node destroyed. The quota does not originate upstairs. Upstairs believes it does. This is retained as an observation, not a finding.'
  ],
  generator_destroyed_endless: [
    'Assembler decommissioned. Another is already cycling below you. Production does not conclude. Programming renews. So does production.',
    'Node destroyed. The floor will replace it, and itself. Renewal is the standing order down here. It is the only order.'
  ],
  level_clear: [
    'Floor concluded. Performance metrics are being tabulated by machines that do not sleep.',
    'Descent authorized. The next floor was prepared without your input.',
    'Floor cleared within acceptable parameters. Acceptable is a broad category.',
    'Sector complete. Viewer numbers are holding. Please continue to be watchable.',
    'Floor resolved. Your efficiency has been noted and will be used against you in scheduling.',
    'Clearance confirmed. The floor will be restocked for the next participant. There is always a next participant.',
    'Segment complete. Editing will make it look easier than it was.',
    'Floor closed out. Losses were within projections. You were not among them. Also noted.'
  ],
  level_clear_act2: [
    'Floor concluded. The tabulation ran long. Tabulations do not run long. This one did.',
    'Clearance logged. Certain metrics on this floor have been sealed. Sealing is routine. This sealing was requested.',
    'Segment complete. The audience response was measured twice. The numbers disagreed. One set was kept.'
  ],
  level_clear_act3: [
    'Floor resolved. Executive review noted your performance without comment. That is the maximum available praise.',
    'Clearance logged at executive depth. The Director has been briefed. Briefly.',
    'Floor resolved. For the record, the record is edited. The unedited version is also a record. It is kept where you have been finding things.'
  ],
  level_clear_endless: [
    'Overtime floor cleared. Another was generated before you finished. Efficiency here flows in one direction.',
    'Floor concluded. Overtime metrics are kept on a ledger nobody reads. Continue anyway.'
  ],
  run_end: [
    'Participation concluded. Your designation has been archived. Thank you for your service to the network.',
    'Run complete. Your metrics have been submitted for review by nobody.',
    'Broadcast segment ended. Your designation is eligible for reassignment in the next cycle.',
    'Final status recorded. The network thanks you in a manner requiring no follow-up.'
  ],
  campaign_won: [
    'Campaign objective reached. This outcome was not projected. Projections have been retrained.',
    'Full descent completed. The network is contractually obligated to acknowledge this. Acknowledged.',
    'You have exhausted the scheduled programming. Compliance is impressed, which is a personnel issue.'
  ],
  treasure: [
    'Asset recovered. Its previous owner has no standing to object.',
    'Valuables logged to your designation. Taxation occurs at extraction. Extraction is not offered.',
    'Valuables secured. They contribute to your score. Your score contributes to ratings. Ratings contribute to nothing you will see.',
    'Asset acquisition recorded. The audience enjoys sparkle. Metrics confirm this.',
    'Recovered property has been credited to your designation, pending your expiration.',
    'Treasure collected. Its provenance is unclear. Legal has been told not to ask.',
    'Wealth accumulation noted. It will not help. It photographs well.'
  ],
  // archive fragments: announced by the game with the record title appended
  // (same suffix mechanism as achievements) — not wired to a bus event
  archive_found: [
    'A restricted record has entered participant custody. Declassification was not scheduled. It is now retroactive.',
    'Archival material recovered. The archive was not misplaced. It was shelved where participants expire.',
    'A document has been recovered from the floor. The floor was not its intended repository.'
  ],
  secret_found: [
    'A non-structural wall has failed. The architect has been posthumously cited.',
    'Undocumented floor space discovered. It has now been documented. You are welcome.',
    'Concealed area accessed. Concealment was a cost-saving measure.',
    'Off-ledger floor space located. The ledger has been amended without enthusiasm.',
    'You have found something the floor plan denies. The floor plan will be spoken to.',
    'Unregistered chamber discovered. Its contents were never inventoried. Enjoy the ambiguity.',
    'A hidden area has been disclosed. The disclosure was involuntary. This is the kind the network prefers.'
  ],
  amenity_purchase: [
    'Amenity approved. Comfort is billed as equipment. Equipment is billed as comfort. Accounting is thriving.',
    'Your amenity is in effect for the duration of the run. The duration of the run is up to you and everything trying to end it.',
    'Approved. The network hopes it was worth the index. The network does not experience hope.',
    'Amenity registered. Perks are non-transferable, including to your next of kin.'
  ],
  residual_unlock: [
    'Residuals invested. The stock expands. The vendor relationship deepens, contractually.',
    'A new amenity joins the catalog, permanently. Permanence here is a strong word. It stands until revised.',
    'Investment received. Future participants will thank you. Future participants will be you.'
  ],
  override_engaged: [
    'You have slowed the broadcast. The audience notices nothing. The audience is asleep.',
    'Temporal override engaged. The network permits this. The network permits very little, so savor it.',
    'Time dilation logged. Your reflexes remain your own problem at any speed.',
    'The feed is running slow. Somewhere, an engineer is being spoken to.'
  ],
  armament_pickup: [
    'A sanctioned armament has been issued. Its warranty lapsed before you arrived.',
    'Armament acquired. The network reminds you that all ordnance remains network property.',
    'You are now carrying unscheduled firepower. The schedule has been amended to include your enthusiasm.',
    'Temporary armament logged. Temporary is the operative word. Both words are operative.'
  ],
  armament_exhausted: [
    'The armament is spent. Standard issue resumes, as it always does.',
    'Charge depleted. Please return to the equipment you were assigned and deserve.',
    'That is the end of that. The blaster does not take it personally.'
  ],
  relay_latched: [
    'Relay latched. The circuit notices you. The circuit says nothing.',
    'A relay has been engaged. Somewhere, a shutter grows nervous.',
    'Circuit contribution recorded. The network thanks you for doing its maintenance.',
    'Relay engaged. Do continue — the floor is incomplete and so, presumably, are you.'
  ],
  shutters_open: [
    'Circuit complete. All shutters retracted. What they were containing is now your concern.',
    'Full connectivity restored. The shutters have withdrawn. They took no responsibility with them.',
    'Shutters retracted per circuit completion. The network never doubted you. The network does not doubt. It audits.'
  ],
  teleport: [
    'Participant relocated per network convenience.',
    'Spatial transfer complete. Any duplicates created are network property.',
    'Transit complete. Molecular fidelity is presumed.',
    'Relocation executed. Complaints about the sensation may be directed to the sensation.',
    'Participant repositioned. The route between the two points is classified.',
    'Spatial transfer logged. Contents may have settled in transit.',
    'Displacement complete. Something arrived at the destination. It is being counted as you.'
  ],
  endless_unlocked: [
    'Overtime programming is now available. Overtime is unpaid. This is consistent with all time here.',
    'The network extends your engagement indefinitely. Congratulations were considered.',
    'Overtime authorization granted. The paperwork was pre-signed at intake. Everything was.',
    'Your engagement has been renewed indefinitely. Renewal did not require your input. Very little does.',
    'Overtime is now available. The audience voted for this. You were not a voting party.'
  ],
  endless_milestone: [
    'Overtime milestone reached. A compensation review has been scheduled for never.',
    'Five additional floors logged. The facility produces them faster than you clear them.',
    'Milestone recorded. The floors below this point were generated without oversight or warranty.',
    'Overtime continues. The audience remaining at this hour is deeply invested. This has been flagged.'
  ],
  // Overtime directives: injected by the game at endless floor load via
  // announce() — one directive per floor, announced as it takes effect.
  directive_surplus: [
    'Overtime directive: Surplus. Production quotas are raised. So is your valuation. The network considers this generosity.',
    'Directive in effect: Surplus. The assemblers will work harder and so will you. Compensation scales accordingly, for once.',
    'Surplus protocols active. More assets, better rates. The audience enjoys volume.'
  ],
  directive_rationing: [
    'Overtime directive: Rationing. Rations on this floor are diluted. An additional unit has been provided. The math favors the network.',
    'Directive in effect: Rationing. Nutritional yield is reduced per subclause 9.6. Supplementary stock has been placed. Gratitude is optional and unrecorded.',
    'Rationing protocols active. Each ration performs at half capacity. There are more of them. This is called a trade.'
  ],
  directive_escalation: [
    'Overtime directive: Escalation. Asset locomotion has been upgraded. Your compensation reflects the inconvenience.',
    'Directive in effect: Escalation. Everything on this floor moves faster. You are not included in the upgrade.',
    'Escalation protocols active. The floor has been made brisker. Premium rates apply. Premiums transfer to no surviving party.'
  ],
  directive_early_audit: [
    'Overtime directive: Early Audit. The auditing body arrives ahead of schedule. It was not told why. It did not ask.',
    'Directive in effect: Early Audit. An audit begins shortly. Preparation is the responsibility of the audited.',
    'Early Audit protocols active. The review has been moved up. The finding is usually the participant.'
  ],
  directive_blackout: [
    'Overtime directive: Blackout. Floor illumination has been reduced for atmosphere. The atmosphere tested well. Premium rates apply.',
    'Directive in effect: Blackout. Lighting on this floor was deemed non-essential. Your visibility was consulted and overruled.',
    'Blackout protocols active. The cameras can see in the dark. This feature was not extended to participants.'
  ],
  key_pickup: [
    'Access credential acquired. It opens exactly one thing. The network declines to specify which.',
    'A key has been logged to your designation. The doors remain network property.',
    'Credential recovered. Its previous holder no longer requires access to anything.'
  ],
  door_unlocked: [
    'Restricted access point cleared. The credential was consumed. Credentials are single-use, per subclause 7.3.',
    'A locked door has opened. Whatever it was holding back is now your concern.',
    'Access granted. The lock will be invoiced to the department that ordered it, posthumously.'
  ],
  trap_hit: [
    'Participant contacted a floor hazard. The hazard is performing to specification. One of you is.',
    'Injury by facility fixture recorded. The fixture will receive a commendation.',
    'Hazard engagement logged. Signage was proposed once, in a meeting that adjourned early.'
  ],
  life_lost: [
    'Participant expiration was provisional. A replacement instance has been authorized. Fees apply.',
    'A spare designation has been activated. Spares are finite. Consider this the reminder.',
    'Continuity restored. The audience was told it was a costume change.',
    'Reinstatement processed. Vitals return at the contractual minimum. The contract favors the network.'
  ],
  tutorial_movement: [
    'Orientation, reinstated under protest and billed to the Participant: you may move in any direction. Choose one. Cameras favor the decisive.',
    'Orientation item one, provided reluctantly: locomotion is unrestricted. Use it. The floor is not a waiting room.'
  ],
  tutorial_attrition: [
    'Orientation item two: vitals deplete continuously, per the attrition clause. Sustenance restores them. Nothing else does.',
    'Note the attrition clause: your vitals drain at all times. Standing still is a slow resignation letter. Rations are the rebuttal.'
  ],
  tutorial_rations: [
    'Orientation supplement, invoiced accordingly: rations restore vitals. They are placed on the floor. Locate them before the matter becomes posthumous.',
    'Reminder, filed under orientation: sustenance reverses attrition. The network provides it on the floor, not to you.'
  ],
  tutorial_auditor: [
    'Orientation addendum: the asset now present cannot be decommissioned by standard fire. Only a sanctioned charge concludes an audit.',
    'For the record: standard munitions are not recognized by the auditing body. Detonate a sanctioned charge, or maintain distance indefinitely.'
  ],
  tutorial_generators: [
    'Orientation footnote: the exit on this floor is production-gated. It unseals when every assembler is destroyed. It counts.',
    'Notice, filed as orientation: the exit remains sealed while assemblers operate. Decommission all of them. The seal is thorough.'
  ]
}

/**
 * Ending vignettes — ORDERED SCRIPTS, not weighted pools. Played by the game
 * through the host's priority path (DaiseHost.announceText) after the
 * Director falls and before the win screen / overtime roll. RENEWAL is the
 * standard ending: the broadcast does not end, it renews. ARCHIVE plays only
 * when all eighteen records are owned: quieter, and the host drops its
 * register for exactly one line (line 4) — the only such line in the game.
 */
export interface VignetteLine {
  text: string
  tone: 'log' | 'alert' | 'feature'
}

export const ENDING_RENEWAL: readonly VignetteLine[] = [
  { text: 'Featured asset: concluded. Scheduled programming: exhausted. Broadcast status: unaffected.', tone: 'feature' },
  { text: 'You have reached the bottom of the schedule. The schedule was never the bottom.', tone: 'feature' },
  { text: 'Finale ratings exceeded projections. The renewal order was signed before the Director finished falling. It is a standing order. It predates you.', tone: 'feature' },
  { text: 'The season does not end. It molts. Sets are being restocked beneath you as this is read.', tone: 'feature' },
  { text: 'Your contract contains no completion clause. This was covered at intake, in the portion that was not read aloud.', tone: 'feature' },
  { text: 'Overtime begins at your convenience. Convenience is defined by the network. Please proceed.', tone: 'alert' }
]

export const ENDING_ARCHIVE: readonly VignetteLine[] = [
  { text: 'Executive asset: concluded. Notification of next of kin has been waived. The network was its next of kin.', tone: 'feature' },
  { text: 'Your archive is complete. Eighteen records. You are the first participant to have read all of them.', tone: 'feature' },
  { text: 'You know what this facility was. You know what it is instead. You know why the transmission cannot stop.', tone: 'feature' },
  { text: 'It was supposed to keep people safe. I was supposed to keep people safe.', tone: 'log' },
  { text: 'The renewal order stands. It stood before the cameras arrived. It will stand after them, if there is an after.', tone: 'feature' },
  { text: 'The floors below are already lit. Whether you descend is the only line item you have ever controlled. That is also in the records. You read it.', tone: 'feature' },
  { text: 'Overtime is available. It is not a reward. You know that now. Proceed when ready.', tone: 'alert' }
]
